import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useEmailConfig } from '@/hooks/useEmailConfig';
import { Loader2, Send, X, Plus, User, Mail, Paperclip, Link, EyeOff } from 'lucide-react';
import { KanbanCandidate } from './types';

interface ClientContact {
  id: string;
  name: string;
  email: string | null;
  position: string | null;
}

interface SendToClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCandidates: KanbanCandidate[];
  jobId: string;
  jobTitle: string;
  clientId?: string | null;
  onSuccess?: () => void;
}

export function SendToClientDialog({
  open,
  onOpenChange,
  selectedCandidates,
  jobId,
  jobTitle,
  clientId,
  onSuccess,
}: SendToClientDialogProps) {
  const { toast } = useToast();
  const { primaryConfig: emailConfig, loading: emailConfigLoading } = useEmailConfig();

  const [clientContacts, setClientContacts] = useState<ClientContact[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [customEmails, setCustomEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  
  const [includeShareLinks, setIncludeShareLinks] = useState(true);
  const [includeResumes, setIncludeResumes] = useState(true);
  const [anonymizeByCandidate, setAnonymizeByCandidate] = useState<Record<string, boolean>>({});
  
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingLinks, setGeneratingLinks] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      fetchClientContacts();
    }
    if (open) {
      setSubject(`Candidate Profiles for ${jobTitle}`);
      setAnonymizeByCandidate({});
    }
  }, [open, clientId, jobTitle]);

  const fetchClientContacts = async () => {
    if (!clientId) return;
    
    const { data, error } = await supabase
      .from('client_contacts')
      .select('id, name, email, position')
      .eq('client_id', clientId)
      .not('email', 'is', null);

    if (!error && data) {
      setClientContacts(data);
      // Auto-select primary contacts
      const primary = data.filter(c => c.email);
      setSelectedContactIds(primary.map(c => c.id));
    }
  };

  const toggleContactSelection = (contactId: string) => {
    setSelectedContactIds(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const addCustomEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !customEmails.includes(email)) {
      setCustomEmails(prev => [...prev, email]);
      setNewEmail('');
    }
  };

  const removeCustomEmail = (email: string) => {
    setCustomEmails(prev => prev.filter(e => e !== email));
  };

  const toggleAnonymize = (candidateId: string) => {
    setAnonymizeByCandidate(prev => ({
      ...prev,
      [candidateId]: !prev[candidateId],
    }));
  };

  const handleSend = async () => {
    const recipientEmails = [
      ...clientContacts.filter(c => selectedContactIds.includes(c.id) && c.email).map(c => c.email!),
      ...customEmails,
    ];

    if (recipientEmails.length === 0) {
      toast({ title: 'Please add at least one recipient', variant: 'destructive' });
      return;
    }

    if (!emailConfig) {
      toast({ title: 'Email not configured', description: 'Please configure your email in settings first.', variant: 'destructive' });
      return;
    }

    setSending(true);

    try {
      // Generate share links for candidates if enabled
      const candidateProfiles = await Promise.all(
        selectedCandidates.map(async (candidate) => {
          const isAnonymized = anonymizeByCandidate[candidate.id] || false;
          let shareLink: string | undefined;

          if (includeShareLinks) {
            setGeneratingLinks(true);
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 30); // 30 day expiry

            const { data: linkData } = await supabase
              .from('candidate_share_links')
              .insert({
                candidate_id: candidate.id,
                is_anonymized: isAnonymized,
                expires_at: expiresAt.toISOString(),
              })
              .select('token')
              .single();

            if (linkData) {
              shareLink = `${window.location.origin}/shared/candidate/${linkData.token}`;
            }
          }

          return {
            id: candidate.id,
            first_name: candidate.first_name,
            last_name: candidate.last_name,
            email_primary: candidate.email_primary,
            phone_primary: candidate.phone_primary,
            city: candidate.city,
            ai_skills_summary: candidate.ai_skills_summary,
            shareLink,
            isAnonymized,
          };
        })
      );

      setGeneratingLinks(false);

      const { data, error } = await supabase.functions.invoke('send-candidate-profiles', {
        body: {
          jobId,
          jobTitle,
          candidateProfiles,
          recipientEmails,
          fromEmail: emailConfig.email_address,
          fromName: emailConfig.display_name || 'Recruiter',
          includeResumes,
          subject,
          message,
        },
      });

      if (error) throw error;

      toast({
        title: 'Profiles sent successfully',
        description: `Sent ${selectedCandidates.length} candidate profile(s) to ${recipientEmails.length} recipient(s)`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Error sending profiles:', error);
      toast({
        title: 'Error sending profiles',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
      setGeneratingLinks(false);
    }
  };

  const allRecipients = [
    ...clientContacts.filter(c => selectedContactIds.includes(c.id) && c.email).map(c => c.email!),
    ...customEmails,
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Send Candidates to Client</DialogTitle>
          <DialogDescription>
            Send {selectedCandidates.length} selected candidate{selectedCandidates.length > 1 ? 's' : ''} for "{jobTitle}"
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6 py-4">
            {/* Selected Candidates */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Selected Candidates ({selectedCandidates.length})
              </Label>
              <div className="space-y-2">
                {selectedCandidates.map(candidate => (
                  <div key={candidate.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                    <div>
                      <span className="font-medium">
                        {candidate.first_name} {candidate.last_name}
                      </span>
                      {candidate.email_primary && (
                        <span className="text-sm text-muted-foreground ml-2">
                          ({candidate.email_primary})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`anon-${candidate.id}`} className="text-sm text-muted-foreground flex items-center gap-1">
                        <EyeOff className="h-3 w-3" />
                        Anonymize
                      </Label>
                      <Switch
                        id={`anon-${candidate.id}`}
                        checked={anonymizeByCandidate[candidate.id] || false}
                        onCheckedChange={() => toggleAnonymize(candidate.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Recipients
              </Label>

              {/* Client Contacts */}
              {clientContacts.length > 0 && (
                <div className="space-y-2">
                  <span className="text-sm text-muted-foreground">Client Contacts</span>
                  <div className="space-y-1">
                    {clientContacts.map(contact => (
                      <div
                        key={contact.id}
                        className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50 cursor-pointer"
                        onClick={() => toggleContactSelection(contact.id)}
                      >
                        <Checkbox checked={selectedContactIds.includes(contact.id)} />
                        <div className="flex-1">
                          <span className="font-medium">{contact.name}</span>
                          {contact.position && (
                            <span className="text-sm text-muted-foreground ml-2">({contact.position})</span>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">{contact.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Emails */}
              <div className="space-y-2">
                <span className="text-sm text-muted-foreground">Additional Recipients</span>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email address..."
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomEmail())}
                  />
                  <Button type="button" variant="outline" size="icon" onClick={addCustomEmail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {customEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {customEmails.map(email => (
                      <Badge key={email} variant="secondary" className="gap-1">
                        {email}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-destructive"
                          onClick={() => removeCustomEmail(email)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {allRecipients.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Will send to {allRecipients.length} recipient(s)
                </p>
              )}
            </div>

            {/* Content Options */}
            <div className="space-y-4">
              <Label>Content Options</Label>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <span className="font-medium text-sm">Include Profile Links</span>
                    <p className="text-xs text-muted-foreground">Generate shareable profile links (30-day expiry)</p>
                  </div>
                </div>
                <Switch checked={includeShareLinks} onCheckedChange={setIncludeShareLinks} />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <span className="font-medium text-sm">Attach Resumes</span>
                    <p className="text-xs text-muted-foreground">Include most recent resume as PDF attachment</p>
                  </div>
                </div>
                <Switch checked={includeResumes} onCheckedChange={setIncludeResumes} />
              </div>
            </div>

            {/* Email Content */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Email subject..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message (optional)</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message to the client..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || allRecipients.length === 0 || emailConfigLoading}>
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {generatingLinks ? 'Generating links...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send to Client
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
